// The loan officer one-page application, at
//
//	/:customerId/:encompassLoid/lo-onepage            a new application
//	/:customerId/:encompassLoid/lo-onepage/:loanId    one in progress, at whatever stage
//
// The link is the one the One Page App Setup page builds once the loan
// officer has entered their Encompass user id; the borrower's link is the
// same path ending in /onepage. Signed-in loan officers only (RouteWrapper),
// and only for the customers the server allows.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { message } from "antd";
import { CheckOutlined, LinkOutlined, LockOutlined } from "@ant-design/icons";
import * as com from "../Common.js";
import { LanguagePicker } from "../QuickIntake/GMCCQuickIntake/GMCCQuickIntake";
import "../QuickIntake/GMCCQuickIntake/GMCCOnePager.css";
import "./LOOnePager.css";
import {
  LOStatus,
  getCompanyBranding,
  configureLOLink,
  getLOOnePagerAccess,
  startLOLinkSession,
  getLOStatus,
  retryLOApplication,
  skipLODocuments,
  syncLOApplication,
} from "./loOnePagerClient";
import { Lang, LangContext, TranslateLO, initialLang, rememberLang, translateLO } from "./loOnePagerI18n";
import LOApplicationForm from "./LOApplicationForm";
import LOReview1003 from "./LOReview1003";
import {
  ProcessingFailedView,
  ProcessingView,
  SyncFailedView,
  SyncWaitingView,
  SyncedView,
  SyncingView,
} from "./LOStageViews";

const POLL_FAST_MS = 4000;
const POLL_SLOW_MS = 15000;

const LOOnePagerPage: React.FC = () => {
  const params = useParams<{ customerId?: string; encompassLoid?: string; loanId?: string }>();
  const location = useLocation();
  const history = useHistory();
  const customerId = params.customerId || com.getCustomerId() || "";
  const encompassLoid = params.encompassLoid || "";
  const loanId = params.loanId || "";
  // The application's key travels in the link (?k=); without it the loan id
  // alone opens nothing.
  const key = useMemo(() => new URLSearchParams(location.search).get("k") || "", [location.search]);

  const [lang, setLang] = useState<Lang>(() => initialLang(location.search));
  const t: TranslateLO = useCallback((key, vars) => translateLO(lang, key, vars), [lang]);
  const changeLang = (next: Lang) => {
    setLang(next);
    rememberLang(next);
  };

  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [deniedKey, setDeniedKey] = useState<"notAllowed" | "linkInvalid" | "loadFailed">("notAllowed");
  const [brand, setBrand] = useState<{ logo: string; name: string }>({ logo: "", name: "" });
  const [logoFailed, setLogoFailed] = useState(false);
  const [status, setStatus] = useState<LOStatus | null>(null);
  const [statusError, setStatusError] = useState<string>("");
  const [busy, setBusy] = useState(false);
  // A review the loan officer went back to from a failed sync stays a review
  // until they confirm again.
  const [forceReview, setForceReview] = useState(false);
  const timer = useRef<any>(null);

  useEffect(() => {
    document.documentElement.lang = lang === "zh" ? "zh-CN" : lang;
  }, [lang]);

  useEffect(() => {
    const previous = document.title;
    document.title = `${t("tabTitleLO")}${brand.name ? ` | ${brand.name}` : ""}`;
    return () => {
      document.title = previous;
    };
  }, [t, brand.name]);

  // Trade the link for a session first; only then is anything else asked.
  useEffect(() => {
    let cancelled = false;
    configureLOLink({ customerId, encompassLoid });
    setAllowed(null);
    (async () => {
      try {
        await startLOLinkSession(loanId || undefined, key || undefined);
        const a = await getLOOnePagerAccess();
        if (!cancelled) setAllowed(!!a);
      } catch (e: any) {
        if (cancelled) return;
        setDeniedKey(e?.status === 404 ? "linkInvalid" : e?.status === 403 ? "notAllowed" : "loadFailed");
        setAllowed(false);
      }
    })();
    if (customerId) {
      getCompanyBranding(customerId).then(setBrand).catch(() => {});
    }
    return () => {
      cancelled = true;
    };
  }, [customerId, encompassLoid, loanId, key]);

  const refresh = useCallback(async () => {
    if (!loanId) return;
    try {
      const s = await getLOStatus(loanId);
      setStatus(s);
      setStatusError("");
    } catch (e: any) {
      setStatusError(e?.status === 404 ? t("notFound") : e?.message || t("loadFailed"));
    }
  }, [loanId, t]);

  // Poll while a job runs: quickly while documents are read or the sync is
  // under way, slowly while a sync waits out a lock.
  useEffect(() => {
    if (!loanId) {
      setStatus(null);
      return;
    }
    if (allowed !== true) return; // the session is not there yet
    refresh();
  }, [loanId, allowed, refresh]);

  useEffect(() => {
    clearTimeout(timer.current);
    if (!status) return;
    const stage = status.stage;
    let delay = 0;
    if (stage === "processing" || stage === "syncing") delay = POLL_FAST_MS;
    else if (stage === "sync_waiting") delay = POLL_SLOW_MS;
    if (delay) timer.current = setTimeout(refresh, delay);
    return () => clearTimeout(timer.current);
  }, [status, refresh]);

  const basePath = `/${customerId}/${encompassLoid}/lo-onepage`;
  const applicationPath = (id: string, k: string) => `${basePath}/${id}${k ? `?k=${encodeURIComponent(k)}` : ""}`;

  const copyLink = () => {
    const url = window.location.origin + (loanId ? applicationPath(loanId, key) : basePath);
    const done = () => message.success(t("linkCopied"));
    if (navigator.clipboard?.writeText) navigator.clipboard.writeText(url).then(done, () => message.info(url));
    else message.info(url);
  };

  const run = async (fn: () => Promise<any>, failMsg: string) => {
    setBusy(true);
    try {
      await fn();
      await refresh();
    } catch (e: any) {
      message.error(e?.message || failMsg);
    } finally {
      setBusy(false);
    }
  };

  const onConfirmSync = () =>
    run(async () => {
      await syncLOApplication(loanId);
      setForceReview(false);
    }, t("syncStartFailed"));
  const onRetry = () => run(() => retryLOApplication(loanId), t("loadFailed"));
  const onSkip = () => run(() => skipLODocuments(loanId), t("loadFailed"));

  const borrowerName = status?.borrower ? `${status.borrower.first_name} ${status.borrower.last_name}`.trim() : "";

  const logo = () => {
    if (brand.logo && !logoFailed) {
      const src = /^(https?:|data:|\/)/.test(brand.logo) ? brand.logo : `data:image/png;base64, ${brand.logo}`;
      return <img src={src} alt={brand.name || "Company Logo"} style={{ height: 40, maxWidth: 250, objectFit: "contain" }} onError={() => setLogoFailed(true)} />;
    }
    if (brand.name) return <div style={{ fontSize: 22, fontWeight: 600, color: "#1F3988" }}>{brand.name}</div>;
    return <div />;
  };

  const stage = status ? (forceReview && status.stage === "sync_failed" ? "review" : status.stage) : "";

  const stepper = useMemo(() => {
    if (!status) return null;
    const order = ["info", "process", "review", "sync"];
    const labels: Record<string, string> = { info: t("stepInfo"), process: t("stepProcess"), review: t("stepReview"), sync: t("stepSync") };
    const currentIndex =
      stage === "processing" || stage === "processing_failed" ? 1 : stage === "review" ? 2 : stage === "form" ? 0 : 3;
    const warn = stage === "processing_failed" || stage === "sync_waiting" || stage === "sync_failed";
    const done = stage === "synced";
    return (
      <div className="lop-stepper">
        {order.map((k, i) => {
          const cls =
            done || i < currentIndex ? "is-done" : i === currentIndex ? (warn ? "is-warn" : "is-current") : "";
          return (
            <React.Fragment key={k}>
              {i > 0 ? <span className="lop-step-line" /> : null}
              <span className={`lop-step ${cls}`}>
                {cls === "is-done" ? <CheckOutlined rev={undefined} /> : null}
                {cls === "is-warn" && stage !== "processing_failed" ? <LockOutlined rev={undefined} /> : null}
                {labels[k]}
              </span>
            </React.Fragment>
          );
        })}
      </div>
    );
  }, [status, stage, t]);

  const body = () => {
    if (allowed === false) {
      return (
        <div className="lop-card-wrap">
          <div className="lop-card">
            <div className="lop-card-head">
              <div className="lop-card-title">{t(deniedKey)}</div>
            </div>
          </div>
        </div>
      );
    }
    if (allowed === null) return null;
    if (!loanId) {
      return <LOApplicationForm t={t} lang={lang} onSubmitted={(id, k) => history.push(applicationPath(id, k))} />;
    }
    if (statusError) {
      return (
        <div className="lop-card-wrap">
          <div className="lop-card">
            <div className="lop-card-head">
              <div className="lop-card-title">{statusError}</div>
            </div>
          </div>
        </div>
      );
    }
    if (!status) return null;
    switch (stage) {
      case "processing":
      case "form":
        return <ProcessingView status={status} t={t} />;
      case "processing_failed":
        return <ProcessingFailedView status={status} t={t} busy={busy} onRetry={onRetry} onSkip={onSkip} />;
      case "review":
        return <LOReview1003 loanId={loanId} status={status} t={t} busy={busy} onConfirm={onConfirmSync} />;
      case "syncing":
        return <SyncingView t={t} />;
      case "sync_waiting":
        return <SyncWaitingView status={status} t={t} lang={lang} busy={busy} onRetry={onRetry} />;
      case "sync_failed":
        return <SyncFailedView status={status} t={t} lang={lang} busy={busy} onRetry={onRetry} onBack={() => setForceReview(true)} />;
      case "synced":
        return <SyncedView status={status} t={t} lang={lang} onStartAnother={() => history.push(basePath)} />;
      default:
        return null;
    }
  };

  return (
    <LangContext.Provider value={lang}>
      <div className="gop lop">
        <header className="gop-header">
          <div className="gop-logo">{logo()}</div>
          <div className="gop-header-tools">
            <LanguagePicker lang={lang} onChange={changeLang} />
          </div>
        </header>

        {loanId && status ? (
          <div className="lop-strip">
            <div className="lop-strip-crumb">
              <strong>{borrowerName}</strong>
              <span>/</span>
              <span>
                {t("loanLabel")} <code>{status.loan_id}</code>
              </span>
            </div>
            <div className="lop-strip-right">
              <span>{t("progressSaved")}</span>
              <button type="button" className="lop-btn-ghost" onClick={copyLink}>
                <LinkOutlined rev={undefined} />
                {t("copyLink")}
              </button>
            </div>
          </div>
        ) : null}

        {loanId && status && stage !== "review" ? (
          <div className="lop-page-main">
            {stepper}
            {body()}
          </div>
        ) : loanId && status && stage === "review" ? (
          <>
            <div className="lop-page-main" style={{ paddingBottom: 0 }}>
              {stepper}
            </div>
            {body()}
          </>
        ) : (
          <div className={loanId ? "lop-page-main" : ""}>{body()}</div>
        )}
      </div>
    </LangContext.Provider>
  );
};

export default LOOnePagerPage;
